import React, { useContext, useMemo } from 'react';
import classNames from 'classnames';
import { Listing } from 'models/Listing';
import { useTranslation } from 'react-i18next';
import CurrentUserContext from 'providers/CurrentUser/CurrentUser.context';
import AuthContext from 'providers/Auth/Auth.context';
import utils, {
  checkIsUrgent,
  getAcceptedListingContacts,
  getIsListingOwner,
  getPortfolioRoute,
  getResolveUrl,
} from 'utils';
import ResolveImage from 'components/ResolveImage';
import { Button, ImageSlider } from 'ncoded-component-library';
import { Link, useNavigate } from 'react-router-dom';
import TextBubleIcon from 'icons/TextBuble.icon';
import ResolvePopup from 'components/ResolvePopup';
import StarIcon from 'icons/Star.icon';
import PlatinumIcon from 'icons/Platinum.icon';
import LinkToIcon from 'icons/LinkTo.icon';
import ShareButton from 'components/ShareButton';
import ListingPrices from 'components/ListingPrices';
import { getImgUrl } from 'components/ResolveImage/utils';
import useMatchMedia from 'hooks/useMatchMedia';
import LocationmarkerIcon from 'icons/Locationmarker.icon';
import EoiDate from 'components/EoiDate';
import ListingContactsCardList from 'components/ListingContactsCardList';
import useLocationMapper from 'router/data/useLocationMapper';
import useShowEnquireAndWatchlist from 'hooks/useShowEnquireAndWatchlist';
import useWatchlist from 'hooks/useWatchlist';

import './PremiumListingCard.styles.scss';
import './PremiumListingCard.styles.responsive.scss';

type PremiumListingCardProps = {
  className?: string;
  listing: Listing;
  onWatchedListed: (value: boolean, listingId: string) => void;
  onEnquire: (listingId: string, redirectLink?: string) => void;
};

const PremiumListingCard: React.FC<PremiumListingCardProps> = (props) => {
  const { className, listing, onWatchedListed, onEnquire } = props;
  const { isLoggedIn } = useContext(AuthContext);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const isPhablet = useMatchMedia('isPhablet');

  const { currentUser } = useContext(CurrentUserContext);
  const currentUserId = currentUser?._id || '';

  const {
    _id: listingId,
    listingTitle,
    location,
    isWatchlisted,
    revenue,
    industry,
    gallery,
    userId,
    slugName,
    averageStockValue,
    highlights,
    businessDescription,
    listingContact,
    EBITDA,
    askingPriceSelect,
    stockAtValue,
    type,
    isEnquired,
    listingEnquiryId,
    dealRoomId,
    locationPostcode,
    seekingExpressionOfInterestDate,
    isCoOwner,
    currency,
    listingContacts,
    dealLocation,
  } = listing;

  const classes = classNames('premium-listing-card', className);

  const isUrgentListing = checkIsUrgent(type);

  const isListingOwner = getIsListingOwner(currentUserId, userId, isCoOwner);

  const firmLogo = listingContact?.firmLogo?.url;

  const { locationLabelMapper } = useLocationMapper();

  const redirectionListingLink = utils.composeBusinessUrl(
    industry,
    slugName,
    type,
  );

  const { showEnquire } = useShowEnquireAndWatchlist(listing);

  const acceptedListingContacts = useMemo(
    () => getAcceptedListingContacts(listingContacts),
    [listingContacts],
  );

  const { loading: watchlistRequestLoading, onWatchlist } = useWatchlist(
    listingId,
    dealRoomId,
    isWatchlisted,
    onWatchedListed,
  );

  return (
    <div className={classes}>
      <span className="premium-listing-card__tier-badge">
        <PlatinumIcon />
        {t('platinum')}
      </span>
      <div className="premium-listing-card__content">
        <div className="premium-listing-card__content__main-content">
          <Link
            className="premium-listing-card__content__main-content__title"
            to={redirectionListingLink}
          >
            {listingTitle}
          </Link>
          <Link
            to={redirectionListingLink}
            className="premium-listing-card__content__main-content__listing-type"
          >
            <span>
              {isUrgentListing ? (
                <>
                  <LocationmarkerIcon />
                  {locationPostcode}
                </>
              ) : (
                locationLabelMapper[location?.state] || dealLocation
              )}
            </span>
            <span>{industry}</span>
          </Link>
          <EoiDate
            seekingExpressionOfInterestDate={seekingExpressionOfInterestDate}
          />
          <ListingPrices
            askingPrice={askingPriceSelect}
            amount={listing?.amount}
            amountFrom={listing?.amountFrom}
            amountTo={listing?.amountTo}
            revenue={revenue}
            EBITDA={EBITDA}
            averageStockValue={averageStockValue}
            stockAtValue={stockAtValue}
            currency={currency}
          />
          <Link
            to={redirectionListingLink}
            className="premium-listing-card__content__main-content__description"
          >
            {businessDescription}
          </Link>
          {!!highlights.length && (
            <ul className="premium-listing-card__content__main-content__highlights">
              {highlights.slice(0, 2).map((highlight, i) => (
                <li key={i}>
                  <div>
                    <Link to={redirectionListingLink}>{highlight}</Link>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="premium-listing-card__content__image-container">
          {gallery?.length > 1 ? (
            <ImageSlider
              onImagePress={() => {
                navigate(redirectionListingLink);
              }}
              slides={gallery.map(({ url }) => ({
                imageUrl: getImgUrl(url, '480'),
              }))}
              showDots={!!gallery?.length}
              className="premium-listing-card__image-container__slider"
            />
          ) : (
            <ResolveImage
              className="premium-listing-card__image-container__image"
              src={getImgUrl(gallery?.[0]?.url, '480')}
              shouldLazyLoad={false}
            />
          )}
        </div>

        <div className="premium-listing-card__content__agent-details">
          <ListingContactsCardList listingContacts={acceptedListingContacts} />

          {firmLogo && (
            <ResolveImage
              asCompanyLogo
              className="premium-listing-card__content__agent-details__image"
              src={getImgUrl(firmLogo, '320')}
              shouldLazyLoad={false}
            />
          )}
        </div>

        <div className="premium-listing-card__content__actions">
          <div>
            {showEnquire && (
              <Button
                variant="outline"
                onClick={() => onEnquire(listingId, redirectionListingLink)}
                icon={TextBubleIcon}
              >
                {t('enquire')}
              </Button>
            )}
            {(isListingOwner || isEnquired) && (
              <Link
                className="as-button ncoded-button--link"
                to={getPortfolioRoute(
                  !isListingOwner,
                  dealRoomId,
                  listingEnquiryId,
                )}
              >
                <LinkToIcon />
                {t(isPhablet ? 'viewPortfolio' : 'viewInMyPortfolio')}
              </Link>
            )}
            <ResolvePopup
              popupContent={t(
                isListingOwner
                  ? 'thisIsYourListing'
                  : isLoggedIn
                  ? isWatchlisted
                    ? 'removeWatchlistedListing'
                    : 'watchlistListing'
                  : 'loginToWatchlist',
              )}
              onHover
            >
              <Button
                className={classNames('watchlist-button', {
                  'watch-listed': isWatchlisted,
                })}
                variant="link"
                icon={<StarIcon />}
                disabled={isListingOwner || watchlistRequestLoading}
                onClick={onWatchlist}
              >
                {t('shortlist')}
              </Button>
            </ResolvePopup>
            <ShareButton url={getResolveUrl(redirectionListingLink)} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumListingCard;
